import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ClipboardList, Check, Circle, TrendingUp } from "lucide-react";

const STATUS_TONE = { open: "outline", in_progress: "warning", complete: "success" };

export default function SupplierActionPlans() {
  const [plans, setPlans] = useState([]);

  const load = () => api.get("/supply-chain/action-plans").then((r) => setPlans(r.data.action_plans || []));
  useEffect(() => { load(); }, []);

  const toggle = async (plan, idx) => {
    const actions = plan.actions.map((a, i) => i === idx ? { ...a, status: a.status === "complete" ? "open" : "complete" } : a);
    try { await api.patch(`/supply-chain/action-plans/${plan.id}`, { actions }); load(); }
    catch (e) { toast.error("Update failed"); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="action-plans-page">
      <div>
        <div className="label-eyebrow mb-1">Supply Chain</div>
        <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><ClipboardList className="h-6 w-6 text-accent" /> Improvement & Action Plans</h2>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Structured plans to upgrade a supplier's data tier (e.g. spend-based → PCF) and close ESG gaps. Tick actions to track progress.</p>
      </div>

      <div className="space-y-3">
        {plans.map((p) => (
          <div key={p.id} className="card-flat p-5" data-testid={`plan-${p.id}`}>
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="font-display font-bold">{p.title}</div>
                <div className="text-[10px] font-mono text-muted-foreground">{p.supplier_name}{p.target_tier ? ` · target tier ${p.target_tier}` : ""}</div>
                {p.issue && <div className="text-[11px] text-muted-foreground mt-1">{p.issue}</div>}
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 text-accent"><TrendingUp className="h-4 w-4" /><span className="font-display text-2xl font-bold">{p.progress_pct}%</span></div>
                <Badge variant={STATUS_TONE[p.status]} className="text-[8px] uppercase">{p.status.replace(/_/g, " ")}</Badge>
              </div>
            </div>
            <div className="h-1.5 w-full rounded-full bg-surface overflow-hidden mt-3 mb-3">
              <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${p.progress_pct}%` }} />
            </div>
            <div className="space-y-1.5">
              {p.actions.map((a, i) => (
                <button key={i} onClick={() => toggle(p, i)} className="w-full flex items-start gap-2 text-left p-2 rounded-sm hover:bg-surface transition-colors" data-testid={`plan-${p.id}-action-${i}`}>
                  {a.status === "complete" ? <Check className="h-4 w-4 text-[#00E676] shrink-0 mt-0.5" /> : <Circle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <div className={`text-xs ${a.status === "complete" ? "line-through text-muted-foreground" : ""}`}>{a.text}</div>
                    <div className="text-[9px] font-mono text-muted-foreground">{a.owner}{a.due ? ` · due ${a.due}` : ""}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
        {!plans.length && <div className="card-flat p-8 text-center text-muted-foreground text-sm">No action plans yet.</div>}
      </div>
    </div>
  );
}
